import type {
  BookLanguage,
  TtsModel,
  Voice,
  VoiceLibraryEntry,
  VoiceLibraryResponse,
} from "@contract";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../../api/fetcher";
import { queryKeys } from "../../../api/keys";
import { Button } from "../../../components/control/Button";
import { Select } from "../../../components/control/Select";
import { TextInput } from "../../../components/control/TextInput";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";

const initialFilters = {
  search: "",
  accent: "",
  gender: "",
  age: "",
  useCase: "",
  sort: "usage_character_count_1y",
  includeHigherRates: false,
};
export function VoiceDemo({
  url,
  language,
  model,
}: {
  url: string | null;
  language?: string | null;
  model?: string | null;
}) {
  const { t } = useTranslation("casting");
  const [open, setOpen] = useState(false);
  const player = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const stopOthers = (event: Event) => {
      if (event.target !== player.current) player.current?.pause();
    };
    document.addEventListener("play", stopOthers, true);
    return () => document.removeEventListener("play", stopOthers, true);
  }, []);
  return (
    <div className="space-y-2">
      <Button variant="quiet" size="sm" disabled={!url} onClick={() => setOpen(!open)}>
        {t("library.listen")}
      </Button>
      {!url ? <span className="text-data text-ink-2">{t("library.noDemo")}</span> : null}
      {open && url ? (
        <>
          {/* biome-ignore lint/a11y/useMediaCaption: provider demos do not include transcripts. */}
          <audio
            ref={player}
            className="max-w-full"
            src={url}
            controls
            autoPlay
            preload="none"
            aria-label={t("library.demo")}
          />
          <p className="text-data text-ink-2">
            {t("library.demoDetails", {
              language: language ?? t("unknownLabel"),
              model: model ?? t("unknownLabel"),
            })}
          </p>
        </>
      ) : null}
    </div>
  );
}
export function RecommendedVoices({
  voices,
  selectedId,
  disabled,
  onSelect,
  onTry,
}: {
  voices: Voice[];
  selectedId?: string | undefined;
  disabled: boolean;
  onSelect: (v: Voice) => void;
  onTry: (v: Voice) => void;
}) {
  const { t } = useTranslation("casting");
  return (
    <section className="px-5 py-4" aria-label={t("library.recommended")}>
      <h2 className="font-serif text-title">{t("library.recommended")}</h2>
      <p className="mt-1 text-data text-ink-2">{t("library.curated")}</p>
      <ul className="mt-3 divide-y divide-line">
        {voices
          .filter((v) => v.recommended !== false)
          .map((voice) => (
            <li key={voice.id} className="py-3">
              <label className="flex items-start gap-3">
                <input
                  type="radio"
                  name="recommended-voice"
                  className="mt-1"
                  checked={voice.id === selectedId}
                  disabled={disabled}
                  onChange={() => onSelect(voice)}
                />
                <span>
                  <span className="font-medium">{voice.name}</span>
                  <span className="block text-data text-ink-2">
                    {[voice.labels.accent, voice.labels.description, voice.labels.gender]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
              </label>
              <div className="mt-2 flex flex-wrap items-start gap-3">
                <VoiceDemo url={voice.previewUrl} language={voice.language} />
                <Button
                  size="sm"
                  variant="quiet"
                  disabled={disabled || voice.rate === null}
                  onClick={() => onTry(voice)}
                >
                  {t("library.tryBook")}
                </Button>
              </div>
            </li>
          ))}
      </ul>
    </section>
  );
}
export function VoiceLibrary({
  bookId,
  language,
  model,
  disabled,
  selectedId,
  onSelect,
}: {
  bookId: string;
  language: BookLanguage;
  model: TtsModel;
  disabled: boolean;
  selectedId?: string | undefined;
  onSelect: (voice: Voice, tryBook: boolean) => void;
}) {
  const { t } = useTranslation("casting");
  const client = useQueryClient();
  const storageKey = `voice-library:${bookId}:${language}`;
  const [filters, setFilters] = useState(() => {
    try {
      return {
        ...initialFilters,
        ...JSON.parse(sessionStorage.getItem(storageKey) ?? "{}"),
      } as typeof initialFilters;
    } catch {
      return initialFilters;
    }
  });
  const [query, setQuery] = useState(filters);
  const root = useRef<HTMLElement>(null);
  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
    const timer = setTimeout(() => setQuery(filters), 350);
    return () => clearTimeout(timer);
  }, [filters, storageKey]);
  useEffect(() => {
    const pane = root.current?.closest("main");
    if (!pane) return;
    const key = `${storageKey}:scroll`;
    const position = Number(sessionStorage.getItem(key) ?? 0);
    const timer = setTimeout(() => {
      pane.scrollTop = position;
    }, 100);
    const remember = () => sessionStorage.setItem(key, String(pane.scrollTop));
    pane.addEventListener("scroll", remember);
    return () => {
      clearTimeout(timer);
      pane.removeEventListener("scroll", remember);
    };
  }, [storageKey]);
  const library = useInfiniteQuery({
    queryKey: ["voice-library", language, model, query],
    initialPageParam: 0,
    staleTime: 300000,
    gcTime: 3600000,
    queryFn: ({ pageParam, signal }) =>
      api<VoiceLibraryResponse>(
        `/voices/library?${new URLSearchParams({ ...query, includeHigherRates: String(query.includeHigherRates), language, model, page: String(pageParam) })}`,
        { signal },
      ),
    getNextPageParam: (last, pages) => (last.hasMore ? pages.length : undefined),
  });
  const importer = useMutation({
    mutationFn: async ({ entry, tryBook }: { entry: VoiceLibraryEntry; tryBook: boolean }) => {
      const voice = await api<Voice>(`/books/${bookId}/voices/import`, {
        method: "POST",
        body: { providerId: entry.providerId, ownerId: entry.ownerId },
      });
      await client.invalidateQueries({ queryKey: queryKeys.voices(language) });
      onSelect(voice, tryBook);
    },
  });
  const entries = [
    ...new Map(
      (library.data?.pages.flatMap((p) => p.voices) ?? []).map((v) => [v.providerId, v]),
    ).values(),
  ];
  const labels: Record<string, string> = {
    "genders.male": t("library.genders.male"),
    "genders.female": t("library.genders.female"),
    "genders.neutral": t("library.genders.neutral"),
    "ages.young": t("library.ages.young"),
    "ages.middle_aged": t("library.ages.middle_aged"),
    "ages.old": t("library.ages.old"),
    "useCases.narrative_story": t("library.useCases.narrative_story"),
    "useCases.informative_educational": t("library.useCases.informative_educational"),
    "useCases.characters_animation": t("library.useCases.characters_animation"),
    "useCases.conversational": t("library.useCases.conversational"),
    "useCases.advertisement": t("library.useCases.advertisement"),
    "useCases.entertainment_tv": t("library.useCases.entertainment_tv"),
    "sorts.usage_character_count_1y": t("library.sorts.usage_character_count_1y"),
    "sorts.trending": t("library.sorts.trending"),
    "sorts.created_date": t("library.sorts.created_date"),
    "sorts.cloned_by_count": t("library.sorts.cloned_by_count"),
  };
  const options = (values: string[], prefix: string) => [
    { value: "", label: t("library.all") },
    ...values.map((value) => ({ value, label: labels[`${prefix}.${value}`] ?? value })),
  ];
  const sortOptions = [
    "usage_character_count_1y",
    "trending",
    "created_date",
    "cloned_by_count",
  ].map((value) => ({ value, label: labels[`sorts.${value}`] ?? value }));
  const genderOptions = options(["male", "female", "neutral"], "genders");
  const ageOptions = options(["young", "middle_aged", "old"], "ages");
  const useCaseOptions = options(
    [
      "narrative_story",
      "informative_educational",
      "characters_animation",
      "conversational",
      "advertisement",
      "entertainment_tv",
    ],
    "useCases",
  );
  return (
    <section
      ref={root}
      className="border-t border-line px-5 py-5"
      aria-label={t("library.explore", { language: t(`common:bookLanguage.${language}`) })}
    >
      <h2 className="font-serif text-title">
        {t("library.explore", { language: t(`common:bookLanguage.${language}`) })}
      </h2>
      <div className="my-4 grid gap-3 sm:grid-cols-2">
        <TextInput
          label={t("library.search")}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <TextInput
          label={t("accent")}
          value={filters.accent}
          onChange={(e) => setFilters({ ...filters, accent: e.target.value })}
        />
        <Select
          label={t("library.gender")}
          value={filters.gender}
          options={genderOptions}
          onChange={(value) => setFilters({ ...filters, gender: value.target.value })}
        />
        <Select
          label={t("library.age")}
          value={filters.age}
          options={ageOptions}
          onChange={(value) => setFilters({ ...filters, age: value.target.value })}
        />
        <Select
          label={t("library.useCase")}
          value={filters.useCase}
          options={useCaseOptions}
          onChange={(value) => setFilters({ ...filters, useCase: value.target.value })}
        />
        <Select
          label={t("library.sort")}
          value={filters.sort}
          options={sortOptions}
          onChange={(value) => setFilters({ ...filters, sort: value.target.value })}
        />
      </div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={filters.includeHigherRates}
          onChange={(e) => setFilters({ ...filters, includeHigherRates: e.target.checked })}
        />
        {t("library.higherRates")}
      </label>
      <ErrorBanner error={library.error ?? importer.error} />
      {library.isError ? (
        <Button variant="secondary" onClick={() => void library.refetch()}>
          {t("retryVoices")}
        </Button>
      ) : null}
      {library.isPending ? (
        <p role="status" className="py-4">
          {t("library.loading")}
        </p>
      ) : null}
      {!library.isPending && !library.isError && entries.length === 0 ? (
        <p className="py-4">{t("library.empty")}</p>
      ) : null}
      <ul className="mt-4 divide-y divide-line">
        {entries.map((entry) => (
          <li key={entry.providerId} className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-medium">{entry.name}</h3>
              <span className="text-data">
                {entry.recommended ? `${t("library.recommendedBadge")} · ` : ""}
                {entry.rate === null
                  ? t("library.unknownRate")
                  : t("library.rate", { rate: entry.rate })}
              </span>
            </div>
            <p className="mt-1 text-data text-ink-2">
              {[entry.accent, entry.gender, entry.age, entry.useCase].filter(Boolean).join(" · ")}
            </p>
            {entry.description ? <p className="mt-2 text-data">{entry.description}</p> : null}
            {!entry.available ? <p>{t("library.unavailable")}</p> : null}
            <div className="mt-3 flex flex-wrap items-start gap-3">
              <VoiceDemo
                url={entry.previewUrl}
                language={entry.previewLanguage}
                model={entry.previewModel}
              />
              <Button
                size="sm"
                variant="secondary"
                busy={
                  importer.isPending && importer.variables?.entry.providerId === entry.providerId
                }
                disabled={disabled || !entry.available || importer.isPending}
                onClick={() => importer.mutate({ entry, tryBook: false })}
              >
                {entry.providerId === selectedId ? t("selected") : t("library.select")}
              </Button>
              <Button
                size="sm"
                variant="quiet"
                disabled={disabled || !entry.available || entry.rate === null || importer.isPending}
                onClick={() => importer.mutate({ entry, tryBook: true })}
              >
                {t("library.tryBook")}
              </Button>
            </div>
          </li>
        ))}
      </ul>
      {library.hasNextPage ? (
        <Button
          variant="secondary"
          busy={library.isFetchingNextPage}
          disabled={filters !== query || library.isFetching}
          onClick={() => void library.fetchNextPage()}
        >
          {t("library.more")}
        </Button>
      ) : null}
    </section>
  );
}
